'use client'

import classNames from 'classnames'
import { FormEvent, useMemo, useRef, useState } from 'react'
import TagManager from 'react-gtm-module'
import { AButton } from 'ui/components/atoms/a-button'
import { ACheckbox } from 'ui/components/atoms/a-checkbox'
import { AInput } from 'ui/components/atoms/a-input'
import { ASelect } from 'ui/components/atoms/a-select'
import { ATextarea } from 'ui/components/atoms/a-textarea'
import { getIsTestEmail, useSiteInfo } from 'shared'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'

type FormField = {
  id: string
  name: string
  label: string
  handle: string
  instructions: string | null
  required: boolean
  type: string
  displayName: string
  typeName: string
  inputTypeName: string
  placeholder: string | null
  errorMessage: string | null
  labelPosition: string | null
  instructionsPosition: string | null
  cssClasses: string | null
  enableConditions: boolean
  conditions: any | null
  enableContentEncryption: boolean
  visibility: string | null
  containerAttributes: any | null
  inputAttributes: any | null
  defaultValue?: any
  defaultState?: boolean
  checkedValue?: string
  uncheckedValue?: string
  descriptionHtml?: string
}

type FormRow = {
  rowFields: FormField[]
}

type FormPage = {
  name: string
  id: string
  settings: {
    submitButtonLabel: string
    backButtonLabel: string
    showBackButton: boolean
    buttonsPosition: string
  }
  rows: FormRow[]
}

type Captcha = {
  handle: string
  name: string
  value: string
}

type CsrfToken = {
  name: string
  value: string
}

export type CFormieField = {
  id: string
  title: string
  handle: string
  siteId: number
  csrfToken: CsrfToken
  captchas: Captcha[]
  pages: FormPage[]
}

type Props = {
  field?: Array<CFormieField>
  showModal?: Function
  className?: string
  csrfToken: string | undefined
  onSuccessHandler?: Function
}

export const CFormie = ({
  field,
  showModal,
  className,
  csrfToken,
  onSuccessHandler,
}: Props) => {
  const siteInfo = useSiteInfo()

  const { t } = useTranslation('common')
  const isSlovak = siteInfo.iso === 'sk-SK'

  const content = useMemo(() => {
    return field && field.length ? field[0] : null
  }, [field])

  const formRef = useRef<HTMLFormElement>(null)

  const [errors, setErrors] = useState<any>({})
  const [status, setStatus] = useState<string>('default')

  const handleAutopilot = () => {
    const formData = new FormData(formRef.current!)
    const interested = Boolean(
      formData.get('fields[subscription]') ||
        formData.get('fields[gdprAcceptance]') ||
        formData.get('fields[interested]')
    )
    const email = formData.get('fields[email]')
    const firstName = formData.get('fields[firstName]') ?? ''
    const lastName = formData.get('fields[lastName]') ?? ''
    const zipCode = formData.get('fields[zipCode]') ?? ''

    const isTestEmail = getIsTestEmail((email as string) ?? '')
    const isUsOrUk = Boolean(siteInfo.isUS || siteInfo.isUK)

    if (!interested || !isUsOrUk || isTestEmail) {
      return
    }

    const body = {
      contact: {
        Email: email,
        FirstName: firstName,
        LastName: lastName,
        MailingPostalCode: zipCode,
        MailingCountry: siteInfo.isUK ? 'United Kingdom' : siteInfo.name,
      },
    }

    fetch(
      process.env.NEXT_PUBLIC_DISCOVERICL_API_PROXY_URL +
        '/autopilot/captureUser.js',
      {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    ).catch((error) => {
      console.error('Autopilot error', error)
    })
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    // Trigger browser validation
    if (!formRef.current!.reportValidity()) {
      // Form is invalid; do not proceed
      return
    }

    setErrors({})

    const formData = new FormData(formRef.current!)

    const newrelic = (window as any).newrelic
    newrelic?.addPageAction('contactFormSubmitted', {
      requestType: formData.get('requestType'),
      country: formData.get('country'),
    })

    formData.set('submitAction', 'submit')

    fetch('/actions/formie/submissions/submit', {
      method: 'post',
      cache: 'no-cache',
      headers: { accept: 'application/json' },
      body: formData,
    })
      .then((res) => {
        return res.json()
      })
      .then((res) => {
        if (res?.success) {
          onSuccess()
        } else {
          setErrors(res?.errors || {})
          onError()
        }
      })
      .catch((e) => {
        onError()
      })
  }

  const onSuccess = () => {
    setStatus('success')

    onSuccessHandler?.()

    handleAutopilot()

    showModal && showModal()

    TagManager.dataLayer({
      dataLayer: {
        event: 'footer_contact_submitted',
      },
    })

    setTimeout(() => {
      setStatus('default')
      formRef.current!?.reset()
    }, 3e3)
  }

  const onError = () => {
    setStatus('default')

    const newrelic = (window as any).newrelic
    newrelic?.noticeError('contactFormSubmissionError')
  }

  if (!content?.id) {
    return <></>
  }

  return (
    <section className={`c-formie mt-6 ${className}`}>
      <form
        ref={formRef}
        noValidate
        className={classNames(
          'transition-opacity duration-300 ease-out',
          status === 'default' ? '' : 'opacity-50 pointer-events-none'
        )}
        onSubmit={onSubmit}
      >
        <input type="hidden" name="CRAFT_CSRF_TOKEN" defaultValue={csrfToken} />
        <input type="hidden" name="handle" defaultValue={content.handle} />
        <input type="hidden" name="siteId" defaultValue={content.siteId} />

        {content.pages?.map((page: any, i: number) => (
          <div key={i}>
            {page.rows?.map((row: any, j: number) => (
              <div key={j} className="-mx-3 md:flex">
                {row?.rowFields?.map((item: any, k: number) => (
                  <div key={k} className="block w-full px-3 mt-6">
                    <label
                      htmlFor={`fields[${item.handle}]`}
                      className={classNames(
                        'mb-2 uppercase text-button text-neutral-dark-gray',
                        {
                          hidden: siteInfo.isUS || item.displayName === 'Html',
                        }
                      )}
                    >
                      {item.required && '*'} {item.label}
                    </label>

                    {['Field_Agree'].includes(item.typeName) && (
                      <ACheckbox
                        checked={item.defaultValue}
                        invalid={!!errors[item.handle]}
                        label={item.instructions}
                        name={`fields[${item.handle}]`}
                      />
                    )}

                    {['Field_SingleLineText', 'Field_Email'].includes(
                      item.typeName
                    ) && (
                      <AInput
                        invalid={!!errors[item.handle]}
                        name={`fields[${item.handle}]`}
                        placeholder={
                          siteInfo.isUS ? item.label : item.placeholder
                        }
                        required={item.required}
                        type="text"
                        className={
                          siteInfo.isUS
                            ? 'rounded-full !border-neutral-black placeholder-neutral-black'
                            : undefined
                        }
                      />
                    )}

                    {['Field_Dropdown'].includes(item.typeName) && (
                      <ASelect
                        invalid={!!errors[item.handle]}
                        name={`fields[${item.handle}]`}
                        options={
                          siteInfo.isUS
                            ? [
                                {
                                  value: '',
                                  label: item.label,
                                  disabled: false,
                                },
                                ...item.options,
                              ]
                            : item.options
                        }
                        required={item.required}
                        className={
                          siteInfo.isUS
                            ? '!rounded-full !border-neutral-black !text-neutral-black'
                            : undefined
                        }
                      />
                    )}

                    {['Field_MultiLineText'].includes(item.typeName) && (
                      <ATextarea
                        invalid={!!errors[item.handle]}
                        name={`fields[${item.handle}]`}
                        placeholder={
                          siteInfo.isUS ? item.label : item.placeholder
                        }
                        required={item.required}
                        rows={6}
                        className={
                          siteInfo.isUS
                            ? 'rounded-3xl !border-neutral-black placeholder-neutral-black'
                            : undefined
                        }
                      />
                    )}

                    {['Field_Html'].includes(item.typeName) && (
                      <div
                        className="text-neutral-dark-gray text-button w-full [&>p>a]:underline [&>p>a]:text-primary-dark-teal"
                        dangerouslySetInnerHTML={{ __html: item.htmlContent }}
                      />
                    )}

                    {errors[item.handle]?.map(
                      (error: string, index: number) => (
                        <p key={index} className="text-secondary-red">
                          {error}
                        </p>
                      )
                    )}
                  </div>
                ))}
              </div>
            ))}

            <div className="block w-full px-3 mt-6">
              <div className="flex flex-wrap gap-4">
                <AButton color="teal" style="solid" type="submit">
                  {page.settings.submitButtonLabel}
                </AButton>

                {isSlovak && (
                  <AButton
                    color="teal"
                    style="outline"
                    type="link"
                    href="/"
                  >
                    <>{t('contact-form.cancel')}</>
                  </AButton>
                )}
              </div>

              {isSlovak && (
                <div className="mt-6 text-sm text-neutral-dark-gray">
                  <p>{t('contact-form.data-use')}</p>
                </div>
              )}
            </div>

          </div>
        ))}
      </form>
    </section>
  )
}

export { CFormieFragments } from '../../fragments/c-formie'
